import { useDeferredValue, useMemo, useState } from 'react';

import type { TreeDataNode } from 'antd';
import { TREE_GROUP_BY_CROP, TREE_GROUP_BY_FARM } from 'constants/uiConstants';
import { IPaddock } from 'interfaces/IPaddock';
import { normaliseTreeSearch, TreeGroupBy } from 'utils/FarmUtils';

import {
  buildCopyCropTreeData,
  getCropViewDefaultExpandedKeys,
} from './buildCopyCropTreeData';
import { CopyTreeIndex } from './buildCopyTreeData';
import { ShownCheckContext } from './copyTreeChecks';
import { getPaddockExpandedKeys, searchCopyTree } from './copyTreeSearch';

interface UseCopyTreeViewParams {
  /** The Farm view tree, with the Source excluded on Destination. */
  treeData: TreeDataNode[];
  /** Holds every node shown, including the Source document on Destination. */
  lookupIndex: CopyTreeIndex;
  /** The panel's selection index, which ticks are checked against. */
  index: CopyTreeIndex;
  paddocksById: Map<string, IPaddock>;
  paddockNames: Map<string, string>;
  /** Destination: farm and crop rows tick the rows shown under them. */
  allowMultiple: boolean;
  /** Destination's Crop view lists the paddocks with no documents. */
  includeEmptyPaddocks: boolean;
  /** Add as new: Crop view's documents tick their paddocks. */
  paddocksOnly: boolean;
  /** Farm view's own expansion, kept in the panel's side state. */
  expandedKeys: string[];
  onExpandedKeysChange: (keys: string[]) => void;
}

interface ExpansionFor {
  term: string;
  treeData: TreeDataNode[];
  keys: string[];
}

/**
 * One panel's view: Farm or Crop grouping, search, and expansion. Each view
 * keeps its own expansion; while searching, every match opens and neither is
 * changed, so clearing the search brings it back. Only what's shown changes,
 * so ticks stay the same across views and searches.
 */
export const useCopyTreeView = ({
  treeData,
  lookupIndex,
  index,
  paddocksById,
  paddockNames,
  allowMultiple,
  includeEmptyPaddocks,
  paddocksOnly,
  expandedKeys,
  onExpandedKeysChange,
}: UseCopyTreeViewParams) => {
  const [groupBy, setGroupBy] = useState<TreeGroupBy>(TREE_GROUP_BY_FARM);
  const [query, setQuery] = useState('');
  // Deferred, so typing stays responsive while the tree filters behind it.
  const term = normaliseTreeSearch(useDeferredValue(query));
  // Null until the user expands or collapses in Crop view, so every crop starts open.
  const [cropExpandedKeys, setCropExpandedKeys] = useState<string[] | null>(
    null
  );
  // Expansion changed during a search; dropped once the term or the tree changes.
  const [searchExpansion, setSearchExpansion] = useState<ExpansionFor | null>(
    null
  );
  const isCropView = groupBy === TREE_GROUP_BY_CROP;

  const searchedTreeData = useMemo(
    () => searchCopyTree(treeData, lookupIndex, paddocksById, term),
    [treeData, lookupIndex, paddocksById, term]
  );
  // Paddocks the search leaves shown, which an Add as new document's tick is limited to.
  const shownPaddockKeys = useMemo(
    () =>
      term
        ? new Set(
            searchedTreeData.flatMap((farm) =>
              (farm.children ?? []).map((paddock) => String(paddock.key))
            )
          )
        : null,
    [term, searchedTreeData]
  );
  const shownTreeData = useMemo(
    () =>
      isCropView
        ? buildCopyCropTreeData({
            farmTreeData: searchedTreeData,
            lookupIndex,
            paddockNames,
            allowMultiple,
            includeEmptyPaddocks,
            paddocksOnly,
          })
        : searchedTreeData,
    [
      isCropView,
      searchedTreeData,
      lookupIndex,
      paddockNames,
      allowMultiple,
      includeEmptyPaddocks,
      paddocksOnly,
    ]
  );
  // One context for every tick rule, so greying and ticking read the same index.
  const checkContext = useMemo<ShownCheckContext>(
    () => ({ index, allowMultiple, paddocksOnly, shownPaddockKeys }),
    [index, allowMultiple, paddocksOnly, shownPaddockKeys]
  );

  const allOpenKeys = useMemo(
    () =>
      isCropView
        ? getCropViewDefaultExpandedKeys(shownTreeData)
        : getPaddockExpandedKeys(shownTreeData),
    [isCropView, shownTreeData]
  );

  const view = {
    query,
    setQuery,
    groupBy,
    setGroupBy,
    isSearching: !!term,
    treeData: shownTreeData,
    checkContext,
  };

  if (term) {
    return {
      ...view,
      expandedKeys:
        searchExpansion?.term === term &&
        searchExpansion.treeData === shownTreeData
          ? searchExpansion.keys
          : allOpenKeys,
      onExpandedKeysChange: (keys: string[]) =>
        setSearchExpansion({ term, treeData: shownTreeData, keys }),
    };
  }
  if (isCropView) {
    return {
      ...view,
      expandedKeys: cropExpandedKeys ?? allOpenKeys,
      onExpandedKeysChange: setCropExpandedKeys,
    };
  }
  return { ...view, expandedKeys, onExpandedKeysChange };
};
